/**
 * 控件数据契约引导（2026-08-18 核对）：每个控件类型的数据 key 契约 + 示例 JSON。
 * 用于数据绑定面板静态数据/解析预览的引导提示，避免用户用错 key。
 */

export interface WidgetDataGuide {
  type: string;
  label: string;
  /** 数据 key 说明（人类可读） */
  keys: string;
  /** 示例 JSON 字符串（可直接粘贴） */
  example: string;
}

export const WIDGET_DATA_GUIDES: WidgetDataGuide[] = [
  {
    type: 'line-chart',
    label: '折线图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "一月", "value": 150 }, { "label": "二月", "value": 230 }]',
  },
  {
    type: 'bar-chart',
    label: '柱状图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "产品A", "value": 120 }, { "label": "产品B", "value": 200 }]',
  },
  {
    type: 'pie-chart',
    label: '饼图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "甲", "value": 30 }, { "label": "乙", "value": 40 }]',
  },
  {
    type: 'funnel',
    label: '漏斗图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "访问", "value": 1000 }, { "label": "下单", "value": 500 }]',
  },
  {
    type: 'ranking',
    label: '排行榜',
    keys: '数组对象：label(名称) + value(数值)（自动按 value 降序）',
    example: '[{ "label": "设备A", "value": 95 }, { "label": "设备B", "value": 88 }]',
  },
  {
    type: 'indicator',
    label: '指标卡',
    keys: '数组对象：value(数值) + label?(标题)',
    example: '[{ "label": "今日产量", "value": 1234 }]',
  },
  {
    type: 'gauge',
    label: '仪表盘',
    keys: '数组对象：value(数值, 0-100) + label?(标题)',
    example: '[{ "label": "负载率", "value": 72 }]',
  },
  {
    type: 'counter',
    label: '数字翻牌',
    keys: '数组对象：value(数值) + label?(标题)',
    example: '[{ "label": "销售额", "value": 9988 }]',
  },
  {
    type: 'progress',
    label: '进度条',
    keys: '数组对象：value(百分比 0-100) + label?(标题)',
    example: '[{ "label": "完成度", "value": 65 }]',
  },
  {
    type: 'radar',
    label: '雷达图',
    keys: '数组对象：name(系列名) + value(数组指标值)',
    example: '[{ "name": "产品A", "value": [90, 80, 70, 85, 60] }]',
  },
  {
    type: 'scatter',
    label: '散点图',
    keys: '数组对象：x + y（可选 label/value）；或 value: [x, y]',
    example: '[{ "x": 10, "y": 20, "label": "点1" }, { "x": 30, "y": 40 }]',
  },
  {
    type: 'candlestick',
    label: 'K线图',
    keys: '数组对象：date(日期) + open/close/low/high(开收低高)',
    example: '[{ "date": "2024-01", "open": 100, "close": 110, "low": 95, "high": 115 }]',
  },
  {
    type: 'sankey',
    label: '桑基图',
    keys: '对象：nodes:[{name}] + links:[{source, target, value}]',
    example: '{"nodes":[{"name":"A"},{"name":"B"}],"links":[{"source":"A","target":"B","value":100}]}',
  },
  {
    type: 'heatmap',
    label: '热力图',
    keys: '二维数组：[x, y, value]',
    example: '[[0, 0, 5], [0, 1, 7], [1, 0, 3], [1, 1, 9]]',
  },
  {
    type: 'map-scatter',
    label: '地图散点',
    keys: '数组对象：name(地名) + value(数值)（可选 lon/lat 经纬度）',
    example: '[{ "name": "北京", "value": 100 }, { "name": "上海", "value": 80 }]',
  },
  {
    type: 'table',
    label: '表格',
    keys: '数组对象：任意字段（自动取对象键为列头）',
    example: '[{ "设备": "A", "温度": 25.5 }, { "设备": "B", "温度": 27.1 }]',
  },
  {
    type: 'treemap',
    label: '矩形树图',
    keys: '数组对象：name(名称) + value(数值)',
    example: '[{ "name": "部门A", "value": 300 }, { "name": "部门B", "value": 200 }]',
  },
  // ---- A-04 批次1：柱/折/饼 变体（9 个） ----
  {
    type: 'pictogram-bar',
    label: '象形柱图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "产品A", "value": 120 }, { "label": "产品B", "value": 200 }]',
  },
  {
    type: 'stacked-bar',
    label: '堆叠柱图',
    keys: '数组对象：label(名称) + v1/v2/v3(多系列数值，key 可在属性面板改)',
    example: '[{ "label": "一月", "v1": 40, "v2": 30, "v3": 20 }, { "label": "二月", "v1": 50, "v2": 25, "v3": 15 }]',
  },
  {
    type: 'hbar',
    label: '水平条形图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "任务A", "value": 85 }, { "label": "任务B", "value": 62 }]',
  },
  {
    type: 'area-line',
    label: '面积折线图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "一月", "value": 150 }, { "label": "二月", "value": 230 }]',
  },
  {
    type: 'multi-line',
    label: '多系列折线图',
    keys: '数组对象：label(名称) + v1/v2/v3(多系列数值，key 可在属性面板改)',
    example: '[{ "label": "一月", "v1": 40, "v2": 30, "v3": 20 }, { "label": "二月", "v1": 50, "v2": 25, "v3": 15 }]',
  },
  {
    type: 'step-line',
    label: '平滑阶梯折线图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "一月", "value": 150 }, { "label": "二月", "value": 230 }]',
  },
  {
    type: 'doughnut',
    label: '环形图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "甲", "value": 30 }, { "label": "乙", "value": 40 }]',
  },
  {
    type: 'rose',
    label: '玫瑰图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "甲", "value": 40 }, { "label": "乙", "value": 30 }]',
  },
  {
    type: 'multi-ring',
    label: '嵌套环形图',
    keys: '数组对象：label(名称) + v1/v2(两层数值，key 可在属性面板改)',
    example: '[{ "label": "A", "v1": 30, "v2": 20 }, { "label": "B", "v1": 40, "v2": 25 }]',
  },
  // ---- A-05 批次2：散点/地图/仪表 扩展（8 个） ----
  {
    type: 'scatter-bubble',
    label: '气泡图',
    keys: '数组对象：x/y(坐标) + size(气泡大小，key 可改) + label?',
    example: '[{ "x": 60, "y": 5, "size": 30, "label": "A" }, { "x": 70, "y": 12, "size": 50, "label": "B" }]',
  },
  {
    type: 'scatter-regression',
    label: '回归散点图',
    keys: '数组对象：x/y(坐标)，自动叠加线性拟合线',
    example: '[{ "x": 1, "y": 2 }, { "x": 2, "y": 4 }, { "x": 3, "y": 5 }, { "x": 4, "y": 9 }]',
  },
  {
    type: 'map-province',
    label: '省级地图',
    keys: '数组对象：name(市级地名，需在对应省 geo 内) + value(数值)；adcode 在属性面板配置',
    example: '[{ "name": "广州市", "value": 120 }, { "name": "深圳市", "value": 98 }]',
  },
  {
    type: 'map-city',
    label: '市级地图',
    keys: '数组对象：name(区县名，需在对应市 geo 内) + value(数值)；adcode 在属性面板配置',
    example: '[{ "name": "天河区", "value": 88 }, { "name": "越秀区", "value": 66 }]',
  },
  {
    type: 'map-migration',
    label: '迁徙地图',
    keys: '数组对象：from(起点地名) + to(终点地名) + value(流量)，在中国地图上画飞线',
    example: '[{ "from": "北京", "to": "上海", "value": 10 }, { "from": "广州", "to": "成都", "value": 8 }]',
  },
  {
    type: 'gauge-multi',
    label: '多表盘',
    keys: '数组对象：label(表盘名) + value(数值, 0-max 可配)',
    example: '[{ "label": "负载率", "value": 72 }, { "label": "温度", "value": 45 }]',
  },
  {
    type: 'funnel-compare',
    label: '对比漏斗',
    keys: '数组对象：label(阶段) + v1/v2(两组漏斗，key 可改)',
    example: '[{ "label": "访问", "v1": 1000, "v2": 800 }, { "label": "下单", "v1": 500, "v2": 420 }]',
  },
  {
    type: 'bar-3d',
    label: '3D 柱状图',
    keys: '数组对象：x(类目) + z(数值) 或 label/value；key 可改',
    example: '[{ "x": "产线A", "z": 80 }, { "x": "产线B", "z": 120 }]',
  },
  // ---- A-06 批次3：扩展图库（8 个） ----
  {
    type: 'liquid',
    label: '水球图',
    keys: '数组对象：value(0~1 水位)',
    example: '[{ "value": 0.65 }]',
  },
  {
    type: 'sunburst',
    label: '旭日图',
    keys: '嵌套对象：name + children:[{name, value}]',
    example: '[{ "name": "根", "children": [{ "name": "A", "value": 30 }, { "name": "B", "value": 20 }] }]',
  },
  {
    type: 'tree',
    label: '树图',
    keys: '嵌套对象：name + children:[{name, value}]（首行含 children 视为根）',
    example: '[{ "name": "根", "children": [{ "name": "子A", "value": 1 }, { "name": "子B", "value": 2 }] }]',
  },
  {
    type: 'wordcloud',
    label: '词云',
    keys: '数组对象：name(词) + value(权重)',
    example: '[{ "name": "DTwince", "value": 100 }, { "name": "数字孪生", "value": 80 }]',
  },
  {
    type: 'sankey-enhanced',
    label: '桑基增强图',
    keys: '数组对象：from(起点) + to(终点) + value(流量)',
    example: '[{ "from": "总览", "to": "车间A", "value": 100 }, { "from": "车间A", "to": "设备1", "value": 60 }]',
  },
  {
    type: 'graph',
    label: '关系图',
    keys: '数组对象：name(节点) + to(关联节点) + value(权重)',
    example: '[{ "name": "A", "to": "B", "value": 5 }, { "name": "B", "to": "C", "value": 3 }]',
  },
  {
    type: 'heatmap-calendar',
    label: '日历热力图',
    keys: '数组对象：date(YYYY-MM-DD) + value(数值 0~100)',
    example: '[{ "date": "2026-01-01", "value": 65 }, { "date": "2026-01-02", "value": 30 }]',
  },
  {
    type: 'radar-advanced',
    label: '多指标雷达图',
    keys: '数组对象：label(维度) + v1/v2/v3(多系列，key 可改)',
    example: '[{ "label": "效率", "v1": 85, "v2": 70 }, { "label": "质量", "v1": 90, "v2": 80 }]',
  },
  // G-00A 批次A（13 个）
  {
    type: 'smooth-line',
    label: '平滑折线图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "一月", "value": 150 }, { "label": "二月", "value": 230 }]',
  },
  {
    type: 'stacked-area',
    label: '堆叠面积图',
    keys: '数组对象：label(名称) + v1/v2/v3(多系列，key 可改)',
    example: '[{ "label": "一月", "v1": 120, "v2": 80 }, { "label": "二月", "v1": 140, "v2": 95 }]',
  },
  {
    type: 'gradient-area',
    label: '渐变面积图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "一月", "value": 150 }, { "label": "二月", "value": 230 }]',
  },
  {
    type: 'radar-fill',
    label: '填充雷达图',
    keys: '数组对象：value(各维度值，按指标顺序)',
    example: '[{ "value": 85 }, { "value": 70 }, { "value": 90 }, { "value": 65 }, { "value": 80 }]',
  },
  {
    type: 'nightingale',
    label: '南丁格尔玫瑰图',
    keys: '数组对象：label(名称) + value(数值)',
    example: '[{ "label": "甲", "value": 40 }, { "label": "乙", "value": 30 }]',
  },
  {
    type: 'pyramid',
    label: '金字塔图',
    keys: '数组对象：label(层级) + value(数值，自动升序排列)',
    example: '[{ "label": "层级A", "value": 100 }, { "label": "层级B", "value": 80 }]',
  },
  {
    type: 'liquid-gradient',
    label: '渐变水球图',
    keys: '数组对象：value(0-1 水位)',
    example: '[{ "value": 0.68 }]',
  },
  {
    type: 'liquid-multi',
    label: '多水位对比图',
    keys: '数组对象：value(0-1 水位，多条同心)',
    example: '[{ "value": 0.7 }, { "value": 0.45 }, { "value": 0.2 }]',
  },
  {
    type: 'hbar-stacked',
    label: '堆叠条形图',
    keys: '数组对象：label(名称) + v1/v2/v3(堆叠系列，key 可改)',
    example: '[{ "label": "任务A", "v1": 30, "v2": 20 }, { "label": "任务B", "v1": 40, "v2": 25 }]',
  },
  {
    type: 'hbar-rank',
    label: '排行条形图',
    keys: '数组对象：label(名称) + value(数值，自动降序取 topN)',
    example: '[{ "label": "设备A", "value": 95 }, { "label": "设备B", "value": 88 }]',
  },
  {
    type: 'heatmap-large',
    label: '大网格热力图',
    keys: '数组对象：value(数值)，行列按顺序铺开（或 x/y/value 三元组）',
    example: '[{ "value": 12 }, { "value": 45 }, { "value": 78 }]',
  },
  {
    type: 'progress-gauge',
    label: '进度仪表盘',
    keys: '数组对象：value(数值，取第一条)',
    example: '[{ "value": 65 }]',
  },
  {
    type: 'calendar',
    label: '日历图',
    keys: '数组对象：date(YYYY-MM-DD) + value(数值)',
    example: '[{ "date": "2026-01-15", "value": 40 }, { "date": "2026-02-03", "value": 80 }]',
  },
  // G-00B 批次B：图表扩展（14 个）
  {
    type: 'waterfall',
    label: '瀑布图',
    keys: '数组对象：label(名称) + value(增减量，负数为下降)',
    example: '[{ "label": "一月", "value": 120 }, { "label": "二月", "value": -40 }]',
  },
  {
    type: 'polar-bar',
    label: '极坐标柱状图',
    keys: '数组对象：label(角度类目) + value(数值)',
    example: '[{ "label": "A", "value": 40 }, { "label": "B", "value": 60 }]',
  },
  {
    type: 'dynamic-bar',
    label: '动态排序柱状图',
    keys: '数组对象：label(名称) + value(数值，自动排序动画)',
    example: '[{ "label": "设备A", "value": 80 }, { "label": "设备B", "value": 60 }]',
  },
  {
    type: 'realtime-line',
    label: '实时折线图',
    keys: '数组对象：value(数值，滚动窗口自动追加)',
    example: '[{ "value": 52 }, { "value": 55 }, { "value": 51 }]',
  },
  {
    type: 'mark-line',
    label: '标记线折线图',
    keys: '数组对象：label(名称) + value(数值)，阈值线在属性面板配置',
    example: '[{ "label": "一月", "value": 150 }, { "label": "二月", "value": 230 }]',
  },
  {
    type: 'ring-progress',
    label: '环形进度图',
    keys: '数组对象：value(0-100 百分比，取第一条)',
    example: '[{ "value": 65 }]',
  },
  {
    type: 'effect-scatter',
    label: '涟漪散点图',
    keys: '数组对象：value([x,y] 坐标) 或 x/y 字段',
    example: '[{ "value": [10, 20] }, { "value": [30, 50] }]',
  },
  {
    type: 'scatter-cluster',
    label: '散点聚合图',
    keys: '数组对象：x/y 坐标（大数据量启用聚合）',
    example: '[{ "x": 10, "y": 20 }, { "x": 30, "y": 50 }]',
  },
  {
    type: 'speedometer',
    label: '速度仪表盘',
    keys: '数组对象：value(数值，取第一条)',
    example: '[{ "value": 88 }]',
  },
  {
    type: 'boxplot',
    label: '箱线图',
    keys: '数组对象：label(类目) + value([min,q1,median,q3,max])',
    example: '[{ "label": "A", "value": [650, 850, 940, 980, 1170] }]',
  },
  {
    type: 'parallel',
    label: '平行坐标图',
    keys: '数组对象：label + v1/v2/v3…（各维度值，维度名可配）',
    example: '[{ "label": "样本1", "v1": 80, "v2": 60, "v3": 70 }]',
  },
  {
    type: 'theme-river',
    label: '主题河流图',
    keys: '数组对象：date(日期) + value(数值) + name(系列名)',
    example: '[{ "date": "2026-01-01", "value": 30, "name": "甲" }]',
  },
  {
    type: 'force-graph',
    label: '力导向关系图',
    keys: '数组对象：from(源) + to(目标) + value(权重)',
    example: '[{ "from": "A", "to": "B", "value": 5 }]',
  },
  {
    type: 'bar-line-mix',
    label: '柱线混合图',
    keys: '数组对象：label + bar(柱值) + line(线值)（key 可改）',
    example: '[{ "label": "一月", "bar": 120, "line": 60 }]',
  },
  // G-00B 批次B：文本/控件（8 个）
  {
    type: 'metric-group',
    label: '指标组',
    keys: '数组对象：label(指标名) + value(数值)，列数可配',
    example: '[{ "label": "产量", "value": 1280 }, { "label": "良率", "value": 98 }]',
  },
  {
    type: 'notice-board',
    label: '公告栏',
    keys: '数组对象：label(公告内容)',
    example: '[{ "label": "系统运行正常" }, { "label": "设备巡检完成" }]',
  },
  {
    type: 'timeline',
    label: '时间轴',
    keys: '数组对象：time(时间) + label(事件)',
    example: '[{ "time": "09:00", "label": "设备启动" }, { "time": "12:00", "label": "产线巡检" }]',
  },
  {
    type: 'qrcode',
    label: '二维码',
    keys: '无需数据；内容在属性面板配置（content）',
    example: '[]',
  },
  {
    type: 'status-light',
    label: '状态灯',
    keys: '数组对象：label(状态名) + value(数值，低于阈值变红)',
    example: '[{ "label": "状态A", "value": 80 }, { "label": "状态B", "value": 30 }]',
  },
  {
    type: 'button-group',
    label: '按钮组',
    keys: '无需数据；按钮在属性面板配置（buttonsJson）',
    example: '[]',
  },
  {
    type: 'slider',
    label: '滑块控件',
    keys: '无需数据；范围在属性面板配置（min/max/step）',
    example: '[]',
  },
  {
    type: 'icon-deco',
    label: '图标装饰',
    keys: '无需数据；图标/颜色/大小在属性面板配置',
    example: '[]',
  },
  // G-00C 批次C（16 个）
  {
    type: 'scatter-3d',
    label: '3D 散点图',
    keys: '数组对象：x/y/z（缺省用 value 兜底）',
    example: '[{ "x": 1, "y": 2, "z": 10 }, { "x": 2, "y": 1, "z": 20 }]',
  },
  {
    type: 'surface-3d',
    label: '3D 曲面图',
    keys: '无需数据；网格密度/透明度在属性面板配置（内置数学曲面）',
    example: '[]',
  },
  {
    type: 'globe-3d',
    label: '3D 地球',
    keys: '数组对象：name + lng/lat(经纬度) + value',
    example: '[{ "name": "北京", "lng": 116.4, "lat": 39.9, "value": 30 }]',
  },
  {
    type: 'flyline-3d',
    label: '3D 飞线图',
    keys: '数组对象：from/to(名称) + fromLng/fromLat/toLng/toLat + value',
    example: '[{ "from": "北京", "to": "上海", "fromLng": 116.4, "fromLat": 39.9, "toLng": 121.47, "toLat": 31.23, "value": 10 }]',
  },
  {
    type: 'map-heatmap',
    label: '地图热力图',
    keys: '数组对象：lng/lat(经纬度) + value(热度)',
    example: '[{ "lng": 116.4, "lat": 39.9, "value": 80 }]',
  },
  {
    type: 'map-bubble',
    label: '地图气泡图',
    keys: '数组对象：name + lng/lat + value(气泡大小)',
    example: '[{ "name": "北京", "lng": 116.4, "lat": 39.9, "value": 30 }]',
  },
  {
    type: 'map-3d',
    label: '3D 地图',
    keys: '无需数据；GeoJSON/视角高度/底色在属性面板配置',
    example: '[]',
  },
  {
    type: 'rich-text',
    label: '富文本',
    keys: '无需数据；HTML 内容在属性面板配置（默认安全清洗）',
    example: '[]',
  },
  {
    type: 'deco-title',
    label: '装饰标题',
    keys: '无需数据；标题/副标题/装饰色在属性面板配置',
    example: '[]',
  },
  {
    type: 'title-banner',
    label: '标题横幅',
    keys: '无需数据；标题/渐变配色在属性面板配置',
    example: '[]',
  },
  {
    type: 'image-carousel',
    label: '图片轮播',
    keys: '属性面板 imagesJson 或数据绑定：url/src + label',
    example: '[{ "url": "https://example.com/1.jpg", "label": "图1" }]',
  },
  {
    type: 'stream-video',
    label: '视频流',
    keys: '无需数据；流地址在属性面板配置（HLS/FLV）',
    example: '[]',
  },
  {
    type: 'iframe-web',
    label: '网页嵌入',
    keys: '无需数据；URL 与白名单在属性面板配置',
    example: '[]',
  },
  {
    type: 'toggle-switch',
    label: '开关',
    keys: '无需数据；默认状态/文案在属性面板配置',
    example: '[]',
  },
  {
    type: 'select-dropdown',
    label: '下拉选择',
    keys: '无需数据；选项在属性面板配置（optionsJson）',
    example: '[]',
  },
  {
    type: 'date-picker',
    label: '日期选择',
    keys: '无需数据；区间/格式在属性面板配置',
    example: '[]',
  },
];

/** 按组件类型取数据契约（未知类型返回 null） */
export function getWidgetDataGuide(type: string): WidgetDataGuide | undefined {
  return WIDGET_DATA_GUIDES.find((g) => g.type === type);
}

/** 静态数据占位符（placeholder）：按当前组件类型给正确示例 */
export function getStaticExampleFor(type: string): string {
  const g = getWidgetDataGuide(type);
  return g ? g.example : '[{ "label": "示例", "value": 100 }]';
}

/** 静态数据提示文案（key 说明） */
export function getStaticHintFor(type: string): string {
  const g = getWidgetDataGuide(type);
  return g ? `${g.label}：${g.keys}` : '通用：数组对象 label + value';
}
